import React, { FormEvent, useEffect, useState } from 'react';
import { Button } from '@mui/material';
import * as yup from 'yup';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import Appbar from 'components/appbar/Appbar';
import InsideSaving from 'components/loading/InsideSaving';
import {
  ContentPad,
  PageShell,
  RecordFooter,
  RecordHeader,
  StyledForm,
  WorkArea,
} from 'components/record-shell/RecordShell';
import { usePermissionRules } from 'hooks/permissionRules';
import { api } from 'services/api';
import history from 'services/history';
import { UserProfile } from 'types/userProfile';
import UserForm, { UserForm as UserFormInterface } from '../form/UserForm';
import { useUserValidation } from '../validation/useUserValidation';

const NewUser: React.FC = () => {
  const { checkPermission } = usePermissionRules();
  const [validation, setValidation, validate] = useUserValidation();
  const [saving, setSaving] = useState(false);
  const [userProfiles, setUserProfiles] = useState<UserProfile[]>([]);
  const [user, setUser] = useState<UserFormInterface>({
    id: 0,
    username: '',
    email: '',
    name: '',
    phone: '',
    rg: '',
    document_number: '',
    role: '',
    active: 1,
    address: '',
    birth_date: null,
    created_at: '',
    email_verified_at: '',
    updated_at: '',
    password: '',
    password_confirmation: '',
    user_profile: {} as UserProfile,
    user_profile_id: 1,
  });

  // sem a permissão de gerir regras de acesso, só o perfil de id 2 pode ser atribuído
  const canPickAnyProfile = checkPermission('settings.user-control.access-rules');
  const profileOptions = canPickAnyProfile ? userProfiles : userProfiles.filter(item => item.id === 2);

  useEffect(() => {
    api
      .get('/user-profiles')
      .then(response => {
        if (response.data.length > 0) {
          setUser(prev => ({ ...prev, user_profile_id: response.data[0].id }));
        }
        setUserProfiles(response.data);
      })
      .catch(err => {
        console.log(err);
      });
  }, []);

  function goBack() {
    history.push('/users');
  }

  function handleChange<K extends keyof UserFormInterface>(index: K, value: UserFormInterface[K]) {
    setUser(prev => ({ ...prev, [index]: value }));
  }

  function handleSubmit() {
    setSaving(true);
    const data = {
      username: user.username,
      email: user.email,
      name: user.name,
      phone: user.phone,
      rg: user.rg,
      document_number: user.document_number,
      role: user.role,
      active: user.active,
      address: user.address,
      user_profile_id: user.user_profile_id,
      birth_date: user.birth_date ? format(user.birth_date, 'yyyy-MM-dd HH:mm:ss') : '',
      password: user.password,
      password_confirmation: user.password_confirmation,
    };

    api
      .post('/users', data)
      .then(response => {
        toast.success('Usuário cadastrado');
        // abre o usuário recém-cadastrado
        history.push(response.data?.id ? `/users/${response.data.id}` : '/users');
      })
      .catch(err => {
        toast.error(err.response?.data?.error || 'Não foi possível cadastrar o usuário');
      })
      .finally(() => setSaving(false));
  }

  function handleValidation(e?: FormEvent<HTMLFormElement>) {
    e?.preventDefault();
    validate(user, 'new')
      .then(() => {
        handleSubmit();
        setValidation({});
      })
      .catch((err: yup.ValidationError) => {
        if (err instanceof yup.ValidationError) {
          setValidation({
            [err.path as string]: err.message,
          });
        }
      });
  }

  return (
    <>
      <Appbar title="Usuários" backAction={goBack} />

      {saving && <InsideSaving />}

      <PageShell>
        <RecordHeader title="Novo usuário" backAction={goBack} />

        <WorkArea>
          <ContentPad>
            <StyledForm onSubmit={handleValidation}>
              <UserForm
                type="new"
                handleChange={handleChange}
                user={user}
                validation={validation}
                userProfiles={profileOptions}
              />
              <button type="submit" style={{ display: 'none' }}></button>
            </StyledForm>
          </ContentPad>
        </WorkArea>

        <RecordFooter>
          <Button variant="outlined" color="inherit" size="small" disabled={saving} onClick={goBack}>
            Cancelar
          </Button>
          <Button variant="contained" size="small" disabled={saving} onClick={() => handleValidation()}>
            Salvar usuário
          </Button>
        </RecordFooter>
      </PageShell>
    </>
  );
};

export default NewUser;
